import { Card, EmptyState } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { DashboardSummary } from '@/services/dashboard.service';

interface OrderTypeMixWidgetProps {
  breakdown: DashboardSummary['order_type_breakdown'];
}

const ORDER_TYPE_META = {
  DINE_IN:  { label: 'Dine-in',  tint: 'from-brand-500 to-indigo-500',   chip: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200' },
  TAKEAWAY: { label: 'Takeaway', tint: 'from-violet-500 to-pink-500',    chip: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200' },
  DELIVERY: { label: 'Delivery', tint: 'from-success-500 to-teal-500',   chip: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200' },
} as const;

/**
 * Restaurant-specific widget — share of today's revenue by order type.
 * Rendered as a horizontal stacked bar so two or three slices read more
 * clearly than a donut at small sizes. Bills with no linked order
 * (counter-only sales) are bucketed as DINE_IN by the backend.
 */
export function OrderTypeMixWidget({ breakdown }: OrderTypeMixWidgetProps) {
  const currency = useTenantCurrency();
  const total = Object.values(breakdown).reduce((s, b) => s + b.revenue, 0);

  if (total === 0) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v18H3V3zm0 6h18M9 9v12" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Order Type Mix</p>
        </div>
        <EmptyState
          variant="inline"
          title="No bills today"
          description="Dine-in, takeaway and delivery split will appear here once orders flow"
        />
      </Card>
    );
  }

  const rows = (Object.keys(ORDER_TYPE_META) as Array<keyof typeof ORDER_TYPE_META>).map((key) => {
    const b = breakdown[key];
    const sharePct = total > 0 ? Math.round((b.revenue / total) * 1000) / 10 : 0;
    return { key, meta: ORDER_TYPE_META[key], ...b, sharePct };
  });

  return (
    <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg animate-slide-up">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v18H3V3zm0 6h18M9 9v12" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Order Type Mix</p>
        </div>
        <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 tabular-nums">
          Today · {formatPaise(total, currency)}
        </span>
      </div>

      {/* Stacked horizontal bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
        {rows.map((r) =>
          r.sharePct > 0 ? (
            <div
              key={r.key}
              className={`h-full bg-gradient-to-r ${r.meta.tint}`}
              style={{ width: `${r.sharePct}%` }}
              title={`${r.meta.label}: ${r.sharePct}%`}
              aria-label={`${r.meta.label} ${r.sharePct}%`}
            />
          ) : null,
        )}
      </div>

      {/* Per-row breakdown */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-col">
            <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.meta.chip}`}>
              {r.meta.label}
            </span>
            <p className="mt-1 font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
              {formatPaise(r.revenue, currency)}
            </p>
            <p className="font-mono tabular-nums text-[11px] text-neutral-500 dark:text-neutral-400">
              {r.bill_count} bills · {r.sharePct}%
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}
